import { useEffect, useState } from "react";
import {
  Users2, TrendingUp, Target, UserCheck, FileText, MessageCircle,
  Mail, Boxes, Wrench, Monitor, Newspaper, Percent,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";
import { PageHeader, Card } from "@/components/admin/AdminUI";

const KPI = [
  { key: "contacts", label: "Contacts", icon: Users2, color: "#3B82F6" },
  { key: "prospects", label: "Prospects", icon: TrendingUp, color: "#06B6D4" },
  { key: "opportunites", label: "Opportunités", icon: Target, color: "#F59E0B" },
  { key: "clients", label: "Clients", icon: UserCheck, color: "#10B981" },
  { key: "quotes", label: "Demandes de devis", icon: FileText, color: "#8B5CF6" },
  { key: "whatsapp", label: "Clics WhatsApp", icon: MessageCircle, color: "#25D366" },
  { key: "subscribers", label: "Abonnés newsletter", icon: Mail, color: "#EC4899" },
  { key: "conversion", label: "Conversion (%)", icon: Percent, color: "#4CB944" },
];
const CATALOG = [
  { key: "solutions", label: "Solutions", icon: Boxes },
  { key: "services", label: "Services", icon: Wrench },
  { key: "products", label: "Produits", icon: Monitor },
  { key: "articles", label: "Articles", icon: Newspaper },
];
const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="text-slate-400">Chargement du tableau de bord…</div>;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble de votre activité commerciale et digitale." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="dashboard-kpis">
        {KPI.map((kpi) => (
          <Card key={kpi.key} className="p-5">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${kpi.color}22` }}><kpi.icon className="w-5 h-5" style={{ color: kpi.color }} /></div>
            </div>
            <div className="mt-4 text-3xl font-bold text-white font-head">{k[kpi.key] ?? 0}{kpi.key === "conversion" ? "%" : ""}</div>
            <div className="text-sm text-slate-400 mt-1">{kpi.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-6">
          <h3 className="font-semibold text-white mb-4">Contacts par source</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.sources}>
              <XAxis dataKey="source" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #1E293B", borderRadius: 12, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="count" fill="#3B82F6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4">Pipeline CRM</h3>
          {data.statuses.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.statuses} dataKey="count" nameKey="status" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3}>
                  {data.statuses.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #1E293B", borderRadius: 12, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="py-16 text-center text-slate-500 text-sm">Aucune donnée</div>}
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <h3 className="font-semibold text-white mb-4">Contacts récents</h3>
          <div className="space-y-2">
            {data.recent_contacts.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Aucun contact pour le moment.</div>}
            {data.recent_contacts.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-xl bg-[#0B0F17]">
                <div><div className="text-sm font-medium text-white">{c.nom} {c.prenom}</div><div className="text-xs text-slate-500">{c.entreprise || "—"} · {c.source}</div></div>
                <span className="text-xs text-slate-400 capitalize">{c.status}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4">Catalogue</h3>
          <div className="space-y-3">
            {CATALOG.map((c) => (
              <div key={c.key} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-slate-300"><c.icon className="w-4 h-4 text-slate-500" />{c.label}</span>
                <span className="text-lg font-bold text-white">{k[c.key] ?? 0}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
